import { createFileRoute, Link } from "@tanstack/react-router";
import { useState, useMemo, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useNavigate } from "@tanstack/react-router";
import { zodValidator } from "@tanstack/zod-adapter";
import { z } from "zod";
import { fallback } from "@tanstack/zod-adapter";
import { dateRangeSearchSchema, dateRangeToBounds } from "@/lib/date-range-search";
import { DateRangeFilter } from "@/components/filters/date-range-filter";
import { DateFieldSelect } from "@/components/filters/date-field-select";
import { SortDirectionToggle } from "@/components/filters/sort-direction-toggle";
import { generateBilling } from "@/lib/billing.functions";
import {
  listInvoices, listInvoiceLines, listInvoiceRules, createInvoice,
  updateInvoiceHeader, setInvoiceStatus, deleteInvoice,
  addInvoiceLine, updateInvoiceLine, deleteInvoiceLine,
} from "@/lib/billing-admin.functions";
import { runServerFn } from "@/lib/server-fn";
import { useScope } from "@/hooks/use-scope";
import { PageHeader } from "@/components/operator-shell";
import { ScopeBar } from "@/components/scope-bar";
import { HelpButton } from "@/components/kb/help-button";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { StatusPill, statusTone } from "@/components/ui/status-pill";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Plus, Loader2, Send, Printer, Settings2, PlayCircle, MoreHorizontal, Pencil, Trash2, Check, X, Ban, DollarSign, TrendingUp, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const billingSearchSchema = dateRangeSearchSchema.extend({
  invoice: fallback(z.string().optional(), undefined),
});

export const Route = createFileRoute("/_authenticated/app/billing/")({
  component: BillingPage,
  validateSearch: zodValidator(billingSearchSchema),
});

const INVOICE_DATE_FIELDS = [
  { value: "created_at", label: "Created" },
  { value: "issued_at", label: "Issued" },
  { value: "due_at", label: "Due" },
  { value: "period_start", label: "Period start" },
  { value: "period_end", label: "Period end" },
] as const;
type InvoiceDateField = (typeof INVOICE_DATE_FIELDS)[number]["value"];

const fmt = (cents: number, ccy: string) =>
  new Intl.NumberFormat(undefined, { style: "currency", currency: ccy || "USD" }).format((cents ?? 0) / 100);

type Invoice = {
  id: string; invoice_number: string; status: string; total_cents: number; subtotal_cents: number;
  currency: string; customer_id: string; period_start: string | null; period_end: string | null;
  issued_at: string | null; due_at: string | null; notes: string | null;
};
type Line = { id: string; description: string; qty: number; unit_price_cents: number; amount_cents: number; source_rule_id: string | null };

function BillingPage() {
  const qc = useQueryClient();
  const { tenantId, customerId, customers } = useScope();
  const navigate = useNavigate({ from: Route.fullPath });
  const generateBillingFn = useServerFn(generateBilling);
  const listInvoicesFn = useServerFn(listInvoices);
  const listLinesFn = useServerFn(listInvoiceLines);
  const listRulesFn = useServerFn(listInvoiceRules);
  const createInvoiceFn = useServerFn(createInvoice);
  const updateHeaderFn = useServerFn(updateInvoiceHeader);
  const setStatusFn = useServerFn(setInvoiceStatus);
  const deleteInvoiceFn = useServerFn(deleteInvoice);
  const addLineFn = useServerFn(addInvoiceLine);
  const updateLineFn = useServerFn(updateInvoiceLine);
  const deleteLineFn = useServerFn(deleteInvoiceLine);
  const tenantCustomers = useMemo(() => customers.filter((c) => c.tenant_id === tenantId), [customers, tenantId]);
  const search = Route.useSearch();
  const [active, setActive] = useState<string | null>(search.invoice ?? null);
  useEffect(() => { if (search.invoice) setActive(search.invoice); }, [search.invoice]);
  const dateField: InvoiceDateField = (search.dateField as InvoiceDateField) ?? "created_at";
  const sortDir: "asc" | "desc" = search.sort ?? "desc";
  const { fromIso, toIso } = dateRangeToBounds({ start: search.start, end: search.end });
  const setSearch = (patch: Record<string, unknown>) =>
    navigate({ search: (prev: any) => ({ ...prev, ...patch }), replace: true });

  // ---- Generate (single)
  const [genOpen, setGenOpen] = useState(false);
  const [genCust, setGenCust] = useState<string>(customerId ?? "");
  const [genStart, setGenStart] = useState("");
  const [genEnd, setGenEnd] = useState("");
  const [generating, setGenerating] = useState(false);
  const runGenerate = async () => {
    const cid = customerId ?? genCust;
    if (!tenantId || !cid || !genStart || !genEnd) return toast.error("Pick customer & dates");
    setGenerating(true);
    try {
      const res = await runServerFn(
        () => generateBillingFn({ data: { tenantId, customerId: cid, periodStart: genStart, periodEnd: genEnd } }),
        "Generate invoice",
      );
      toast.success(`Generated ${res.linesGenerated} lines · $${(res.totalCents / 100).toFixed(2)}`);
      setGenOpen(false);
      qc.invalidateQueries({ queryKey: ["invoices"] });
      if (res.invoiceId) setActive(res.invoiceId);
    } catch {
      // toast already shown
    } finally {
      setGenerating(false);
    }
  };

  // ---- Batch
  const [batchOpen, setBatchOpen] = useState(false);
  const [batchStart, setBatchStart] = useState("");
  const [batchEnd, setBatchEnd] = useState("");
  const [batchRunning, setBatchRunning] = useState(false);
  const [batchResults, setBatchResults] = useState<Array<{ customer: string; lines: number; total_cents: number; error?: string }>>([]);
  const runBatch = async () => {
    if (!tenantId || !batchStart || !batchEnd) return toast.error("Pick dates");
    if (tenantCustomers.length === 0) return toast.error("No customers");
    setBatchRunning(true); setBatchResults([]);
    const out: typeof batchResults = [];
    for (const c of tenantCustomers) {
      try {
        const res = await generateBillingFn({
          data: { tenantId, customerId: c.id, periodStart: batchStart, periodEnd: batchEnd },
        });
        out.push({ customer: c.name, lines: res.linesGenerated, total_cents: res.totalCents });
      } catch (err) {
        out.push({ customer: c.name, lines: 0, total_cents: 0, error: (err as Error).message });
      }
      setBatchResults([...out]);
    }
    setBatchRunning(false);
    toast.success(`Batch complete · ${out.length} customer(s)`);
    qc.invalidateQueries({ queryKey: ["invoices"] });
  };

  // ---- Invoices list
  const { data: invoices, isLoading } = useQuery({
    queryKey: ["invoices", tenantId, customerId, fromIso, toIso, dateField, sortDir],
    enabled: !!tenantId,
    queryFn: () =>
      listInvoicesFn({ data: {
        tenantId: tenantId!, customerId: customerId ?? null,
        fromIso, toIso, sort: sortDir, dateField,
      } }) as Promise<Invoice[]>,
  });
  const cName = (id: string) => customers.find((c) => c.id === id)?.name ?? "—";
  const inv = useMemo(() => invoices?.find((i) => i.id === active) ?? null, [invoices, active]);

  // ---- Lines
  const { data: lines, refetch: refetchLines } = useQuery({
    queryKey: ["invoice-lines", active], enabled: !!active,
    queryFn: () => listLinesFn({ data: { invoiceId: active! } }) as Promise<Line[]>,
  });

  // ---- Available rules (for "add line from rule")
  const { data: rules = [] } = useQuery({
    queryKey: ["billing-rules-for-invoice", tenantId, inv?.customer_id],
    enabled: !!tenantId && !!inv,
    queryFn: () => listRulesFn({ data: { tenantId: tenantId!, customerId: inv!.customer_id } }),
  });

  // ---- Create invoice
  const [open, setOpen] = useState(false);
  const [number, setNumber] = useState(""); const [pStart, setPStart] = useState(""); const [pEnd, setPEnd] = useState("");
  const [formCustomer, setFormCustomer] = useState(""); const [submitting, setSubmitting] = useState(false);
  const create = async (e: React.FormEvent) => {
    e.preventDefault();
    const cid = customerId ?? formCustomer;
    if (!tenantId || !cid) return toast.error("Pick tenant and customer");
    setSubmitting(true);
    try {
      await runServerFn(() => createInvoiceFn({ data: {
        tenantId, customerId: cid, invoiceNumber: number.trim(),
        periodStart: pStart || null, periodEnd: pEnd || null,
      } }), "Create invoice");
      toast.success("Invoice created"); setOpen(false); setNumber(""); setPStart(""); setPEnd(""); setFormCustomer("");
      qc.invalidateQueries({ queryKey: ["invoices"] });
    } catch { /* toast handled */ } finally { setSubmitting(false); }
  };

  // ---- Add line (manual)
  const [lOpen, setLOpen] = useState(false);
  const [desc, setDesc] = useState(""); const [qty, setQty] = useState("1"); const [price, setPrice] = useState(""); const [adding, setAdding] = useState(false);
  const addLine = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!active) return;
    const q = parseFloat(qty); const p = Math.round(parseFloat(price) * 100);
    if (!desc || !q || !p) return toast.error("Fill all fields");
    setAdding(true);
    try {
      await runServerFn(() => addLineFn({ data: {
        invoiceId: active, description: desc.trim(), qty: q, unitPriceCents: p,
      } }), "Add line");
      toast.success("Line added"); setLOpen(false); setDesc(""); setQty("1"); setPrice("");
      qc.invalidateQueries({ queryKey: ["invoices"] });
      refetchLines();
    } catch { /* toast handled */ } finally { setAdding(false); }
  };

  // ---- Add line from rule
  const [rOpen, setROpen] = useState(false);
  const [ruleId, setRuleId] = useState(""); const [ruleQty, setRuleQty] = useState("1");
  const addFromRule = async () => {
    if (!active || !ruleId) return toast.error("Pick a rule");
    const r = (rules as any[]).find((x) => x.id === ruleId);
    if (!r) return;
    const q = parseFloat(ruleQty) || 1;
    try {
      await runServerFn(() => addLineFn({ data: {
        invoiceId: active, description: r.description, qty: q,
        unitPriceCents: r.unit_price_cents, sourceRuleId: r.id,
      } }), "Add line");
      toast.success("Line added"); setROpen(false); setRuleId(""); setRuleQty("1");
      qc.invalidateQueries({ queryKey: ["invoices"] });
      refetchLines();
    } catch { /* toast handled */ }
  };

  // ---- Inline line edit
  const [editingLine, setEditingLine] = useState<string | null>(null);
  const [eDesc, setEDesc] = useState(""); const [eQty, setEQty] = useState(""); const [ePrice, setEPrice] = useState("");
  const startEditLine = (l: Line) => {
    setEditingLine(l.id); setEDesc(l.description); setEQty(String(l.qty)); setEPrice((l.unit_price_cents / 100).toFixed(2));
  };
  const saveLine = async () => {
    if (!editingLine || !active) return;
    const q = parseFloat(eQty); const p = Math.round(parseFloat(ePrice) * 100);
    if (!eDesc || !q || isNaN(p)) return toast.error("Invalid values");
    try {
      await runServerFn(() => updateLineFn({ data: {
        lineId: editingLine, invoiceId: active,
        description: eDesc, qty: q, unitPriceCents: p,
      } }), "Update line");
      setEditingLine(null);
      qc.invalidateQueries({ queryKey: ["invoices"] });
      refetchLines();
    } catch { /* toast handled */ }
  };
  const deleteLine = async (id: string) => {
    if (!active) return;
    try {
      await runServerFn(() => deleteLineFn({ data: { lineId: id, invoiceId: active } }), "Delete line");
      qc.invalidateQueries({ queryKey: ["invoices"] });
      refetchLines();
    } catch { /* toast handled */ }
  };

  // ---- Header edit
  const [editHeader, setEditHeader] = useState(false);
  const [hNumber, setHNumber] = useState(""); const [hStart, setHStart] = useState(""); const [hEnd, setHEnd] = useState("");
  const [hDue, setHDue] = useState(""); const [hCcy, setHCcy] = useState(""); const [hNotes, setHNotes] = useState("");
  useEffect(() => {
    if (inv) {
      setHNumber(inv.invoice_number); setHStart(inv.period_start ?? ""); setHEnd(inv.period_end ?? "");
      setHDue(inv.due_at?.slice(0, 10) ?? ""); setHCcy(inv.currency); setHNotes(inv.notes ?? "");
    }
  }, [inv?.id]);
  const saveHeader = async () => {
    if (!inv) return;
    try {
      await runServerFn(() => updateHeaderFn({ data: {
        invoiceId: inv.id, invoiceNumber: hNumber.trim(),
        periodStart: hStart || null, periodEnd: hEnd || null,
        dueAt: hDue || null, currency: hCcy.trim() || "USD",
        notes: hNotes || null,
      } }), "Save invoice");
      toast.success("Saved"); setEditHeader(false);
      qc.invalidateQueries({ queryKey: ["invoices"] });
    } catch { /* toast handled */ }
  };

  // ---- Status transitions
  const setStatus = async (id: string, status: "draft" | "issued" | "paid" | "void", extra: Record<string, any> = {}) => {
    void extra;
    try {
      await runServerFn(() => setStatusFn({ data: { invoiceId: id, status } }), "Update status");
      toast.success(status);
      qc.invalidateQueries({ queryKey: ["invoices"] });
    } catch { /* toast handled */ }
  };
  const issue = () => inv && setStatus(inv.id, "issued");
  const markPaid = () => inv && setStatus(inv.id, "paid");

  // ---- Void / delete
  const [voidId, setVoidId] = useState<string | null>(null);
  const [delId, setDelId] = useState<string | null>(null);
  const doVoid = async () => {
    if (!voidId) return;
    try {
      await runServerFn(() => setStatusFn({ data: { invoiceId: voidId, status: "void" } }), "Void invoice");
      toast.success("Voided");
      qc.invalidateQueries({ queryKey: ["invoices"] });
    } catch { /* toast handled */ } finally { setVoidId(null); }
  };
  const doDelete = async () => {
    if (!delId) return;
    try {
      await runServerFn(() => deleteInvoiceFn({ data: { invoiceId: delId } }), "Delete invoice");
      if (active === delId) setActive(null);
      toast.success("Deleted");
      qc.invalidateQueries({ queryKey: ["invoices"] });
    } catch { /* toast handled */ } finally { setDelId(null); }
  };

  const editable = inv?.status === "draft";
  const canIssue = inv?.status === "draft";
  const canPay = inv?.status === "issued";
  const canVoid = inv && inv.status !== "paid" && inv.status !== "void";
  const canDelete = inv?.status === "draft";

  return (
    <>
      <PageHeader title="Billing" description="Invoices issued to your brands." actions={
        <div className="flex flex-wrap items-center gap-2">
          <ScopeBar />
          <HelpButton moduleKey="billing" />
          <Button size="sm" variant="outline" asChild>
            <Link to="/app/billing/rules"><Settings2 className="h-4 w-4 mr-2" />Rules</Link>
          </Button>
          <Button size="sm" variant="outline" asChild>
            <Link to="/app/billing/profitability"><TrendingUp className="h-4 w-4 mr-2" />Profitability</Link>
          </Button>
          <Button size="sm" variant="outline" asChild>
            <Link to="/app/billing/anomalies"><AlertTriangle className="h-4 w-4 mr-2" />Anomalies</Link>
          </Button>
        </div>
      } />
      <div className="p-4 sm:p-6 grid gap-4 lg:grid-cols-2">
        <div className="border rounded-lg bg-card">
          <div className="p-3 flex flex-wrap items-center gap-2">
            <DateFieldSelect
              value={dateField}
              onChange={(v) => setSearch({ dateField: v })}
              options={INVOICE_DATE_FIELDS as any}
            />
            <DateRangeFilter
              value={{ start: search.start, end: search.end }}
              onChange={(v) => setSearch({ start: v.start, end: v.end })}
            />
            <SortDirectionToggle value={sortDir} onChange={(v) => setSearch({ sort: v })} />
            <div className="ml-auto flex gap-2">
            <Dialog open={batchOpen} onOpenChange={(o) => { setBatchOpen(o); if (!o) setBatchResults([]); }}>
              <DialogTrigger asChild><Button size="sm" variant="outline"><PlayCircle className="h-4 w-4 mr-2" />Batch run</Button></DialogTrigger>
              <DialogContent className="max-w-lg">
                <DialogHeader><DialogTitle>Run billing for all customers</DialogTitle></DialogHeader>
                <div className="space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div className="space-y-2"><Label>Period start</Label><Input type="date" value={batchStart} onChange={(e) => setBatchStart(e.target.value)} /></div>
                    <div className="space-y-2"><Label>Period end</Label><Input type="date" value={batchEnd} onChange={(e) => setBatchEnd(e.target.value)} /></div>
                  </div>
                  <p className="text-xs text-muted-foreground">Iterates {tenantCustomers.length} customer(s). Re-running replaces auto-generated lines on existing draft invoices and never modifies issued invoices.</p>
                  {batchResults.length > 0 && (
                    <div className="border rounded-md max-h-64 overflow-auto">
                      <Table>
                        <TableHeader><TableRow><TableHead>Customer</TableHead><TableHead className="text-right">Lines</TableHead><TableHead className="text-right">Total</TableHead></TableRow></TableHeader>
                        <TableBody>
                          {batchResults.map((r, i) => (
                            <TableRow key={i}>
                              <TableCell className="font-medium">{r.customer}</TableCell>
                              <TableCell className="text-right tabular-nums">{r.lines}</TableCell>
                              <TableCell className="text-right tabular-nums">${(r.total_cents / 100).toFixed(2)}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  )}
                </div>
                <DialogFooter><Button onClick={runBatch} disabled={batchRunning}>{batchRunning && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Run</Button></DialogFooter>
              </DialogContent>
            </Dialog>
            <Dialog open={genOpen} onOpenChange={setGenOpen}>
              <DialogTrigger asChild><Button size="sm" variant="outline"><PlayCircle className="h-4 w-4 mr-2" />Generate</Button></DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>Generate invoice from rules</DialogTitle></DialogHeader>
                <div className="space-y-4">
                  {!customerId && (
                    <div className="space-y-2"><Label>Customer</Label>
                      <Select value={genCust} onValueChange={setGenCust}>
                        <SelectTrigger><SelectValue placeholder="Pick customer" /></SelectTrigger>
                        <SelectContent>{tenantCustomers.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                  )}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div className="space-y-2"><Label>Period start</Label><Input type="date" value={genStart} onChange={(e) => setGenStart(e.target.value)} /></div>
                    <div className="space-y-2"><Label>Period end</Label><Input type="date" value={genEnd} onChange={(e) => setGenEnd(e.target.value)} /></div>
                  </div>
                </div>
                <DialogFooter><Button onClick={runGenerate} disabled={generating}>{generating && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Generate</Button></DialogFooter>
              </DialogContent>
            </Dialog>
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild><Button size="sm"><Plus className="h-4 w-4 mr-2" />New invoice</Button></DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>Create invoice</DialogTitle></DialogHeader>
                <form onSubmit={create} className="space-y-4">
                  {!customerId && (
                    <div className="space-y-2"><Label>Customer</Label>
                      <Select value={formCustomer} onValueChange={setFormCustomer}>
                        <SelectTrigger><SelectValue placeholder="Pick customer" /></SelectTrigger>
                        <SelectContent>{tenantCustomers.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                  )}
                  <div className="space-y-2"><Label>Invoice number</Label><Input required value={number} onChange={(e) => setNumber(e.target.value)} placeholder="INV-2026-001" /></div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div className="space-y-2"><Label>Period start</Label><Input type="date" value={pStart} onChange={(e) => setPStart(e.target.value)} /></div>
                    <div className="space-y-2"><Label>Period end</Label><Input type="date" value={pEnd} onChange={(e) => setPEnd(e.target.value)} /></div>
                  </div>
                  <DialogFooter><Button type="submit" disabled={submitting}>{submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Create</Button></DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
            </div>
          </div>
          <div className="overflow-x-auto"><Table>
            <TableHeader><TableRow><TableHead>Invoice #</TableHead><TableHead>Customer</TableHead><TableHead className="text-right">Total</TableHead><TableHead>Status</TableHead><TableHead></TableHead></TableRow></TableHeader>
            <TableBody>
              {isLoading ? (<TableRow><TableCell colSpan={5} className="text-center py-8 text-muted-foreground">Loading…</TableCell></TableRow>)
              : invoices && invoices.length > 0 ? invoices.map((i) => (
                <TableRow key={i.id} className={active === i.id ? "bg-accent" : ""}>
                  <TableCell className="font-medium cursor-pointer" onClick={() => setActive(i.id)}>{i.invoice_number}</TableCell>
                  <TableCell className="text-muted-foreground">{cName(i.customer_id)}</TableCell>
                  <TableCell className="text-right tabular-nums">{fmt(i.total_cents, i.currency)}</TableCell>
                  <TableCell><StatusPill tone={statusTone(i.status)}>{i.status}</StatusPill></TableCell>
                  <TableCell className="text-right">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild><Button size="icon" variant="ghost"><MoreHorizontal className="h-4 w-4" /></Button></DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => setActive(i.id)}>Open</DropdownMenuItem>
                        <DropdownMenuSeparator />
                        {i.status !== "paid" && i.status !== "void" && (
                          <DropdownMenuItem onClick={() => setVoidId(i.id)}><Ban className="h-4 w-4 mr-2" />Void</DropdownMenuItem>
                        )}
                        {i.status === "draft" && (
                          <DropdownMenuItem onClick={() => setDelId(i.id)} className="text-destructive"><Trash2 className="h-4 w-4 mr-2" />Delete</DropdownMenuItem>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              )) : (<TableRow><TableCell colSpan={5} className="text-center py-8 text-muted-foreground">No invoices.</TableCell></TableRow>)}
            </TableBody>
          </Table></div>
        </div>

        <div className="border rounded-lg bg-card">
          {!inv ? (
            <div className="p-8 text-center text-muted-foreground text-sm">Select an invoice to manage lines.</div>
          ) : (
            <div className="p-4 space-y-3">
              {!editHeader ? (
                <div className="flex items-start justify-between">
                  <div>
                    <div className="font-semibold flex items-center gap-2">
                      {inv.invoice_number} · {fmt(inv.total_cents, inv.currency)}
                      <StatusPill tone={statusTone(inv.status)}>{inv.status}</StatusPill>
                    </div>
                    <div className="text-sm text-muted-foreground">{inv.period_start ?? "—"} → {inv.period_end ?? "—"}{inv.due_at ? ` · due ${inv.due_at.slice(0,10)}` : ""}</div>
                    {inv.notes && <div className="text-xs text-muted-foreground mt-1">{inv.notes}</div>}
                  </div>
                  <div className="flex gap-2 flex-wrap justify-end">
                    {editable && <Button size="sm" variant="ghost" onClick={() => setEditHeader(true)}><Pencil className="h-4 w-4 mr-2" />Edit</Button>}
                    <Dialog open={lOpen} onOpenChange={setLOpen}>
                      <DialogTrigger asChild><Button size="sm" variant="outline" disabled={!editable}><Plus className="h-4 w-4 mr-2" />Line</Button></DialogTrigger>
                      <DialogContent>
                        <DialogHeader><DialogTitle>Add invoice line</DialogTitle></DialogHeader>
                        <form onSubmit={addLine} className="space-y-4">
                          <div className="space-y-2"><Label>Description</Label><Input required value={desc} onChange={(e) => setDesc(e.target.value)} /></div>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <div className="space-y-2"><Label>Qty</Label><Input type="number" step="0.01" required value={qty} onChange={(e) => setQty(e.target.value)} /></div>
                            <div className="space-y-2"><Label>Unit price</Label><Input type="number" step="0.01" required value={price} onChange={(e) => setPrice(e.target.value)} /></div>
                          </div>
                          <DialogFooter><Button type="submit" disabled={adding}>{adding && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Add</Button></DialogFooter>
                        </form>
                      </DialogContent>
                    </Dialog>
                    <Dialog open={rOpen} onOpenChange={setROpen}>
                      <DialogTrigger asChild><Button size="sm" variant="outline" disabled={!editable || rules.length === 0}>From rule</Button></DialogTrigger>
                      <DialogContent>
                        <DialogHeader><DialogTitle>Add line from billing rule</DialogTitle></DialogHeader>
                        <div className="space-y-4">
                          <div className="space-y-2"><Label>Rule</Label>
                            <Select value={ruleId} onValueChange={setRuleId}>
                              <SelectTrigger><SelectValue placeholder="Pick a rule" /></SelectTrigger>
                              <SelectContent>{(rules as any[]).map((r) => <SelectItem key={r.id} value={r.id}>{r.description} · ${(r.unit_price_cents/100).toFixed(2)} ({r.rule_type})</SelectItem>)}</SelectContent>
                            </Select>
                          </div>
                          <div className="space-y-2"><Label>Qty</Label><Input type="number" step="0.01" value={ruleQty} onChange={(e) => setRuleQty(e.target.value)} /></div>
                        </div>
                        <DialogFooter><Button onClick={addFromRule}>Add</Button></DialogFooter>
                      </DialogContent>
                    </Dialog>
                    {canIssue && <Button size="sm" onClick={issue}><Send className="h-4 w-4 mr-2" />Issue</Button>}
                    {canPay && <Button size="sm" onClick={markPaid}><DollarSign className="h-4 w-4 mr-2" />Mark paid</Button>}
                    <Button size="sm" variant="outline" asChild>
                      <Link to="/app/billing/$invoiceId/print" params={{ invoiceId: inv.id }} target="_blank">
                        <Printer className="h-4 w-4 mr-2" />Print
                      </Link>
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="space-y-3 border rounded-md p-3 bg-muted/30">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div className="space-y-2"><Label>Invoice #</Label><Input value={hNumber} onChange={(e) => setHNumber(e.target.value)} /></div>
                    <div className="space-y-2"><Label>Currency</Label><Input value={hCcy} onChange={(e) => setHCcy(e.target.value)} /></div>
                    <div className="space-y-2"><Label>Period start</Label><Input type="date" value={hStart} onChange={(e) => setHStart(e.target.value)} /></div>
                    <div className="space-y-2"><Label>Period end</Label><Input type="date" value={hEnd} onChange={(e) => setHEnd(e.target.value)} /></div>
                    <div className="space-y-2 col-span-2"><Label>Due date</Label><Input type="date" value={hDue} onChange={(e) => setHDue(e.target.value)} /></div>
                    <div className="space-y-2 col-span-2"><Label>Notes</Label><Textarea value={hNotes} onChange={(e) => setHNotes(e.target.value)} /></div>
                  </div>
                  <div className="flex gap-2 justify-end"><Button size="sm" variant="ghost" onClick={() => setEditHeader(false)}>Cancel</Button><Button size="sm" onClick={saveHeader}>Save</Button></div>
                </div>
              )}
              <div className="overflow-x-auto"><Table>
                <TableHeader><TableRow><TableHead>Description</TableHead><TableHead className="text-right">Qty</TableHead><TableHead className="text-right">Unit</TableHead><TableHead className="text-right">Amount</TableHead><TableHead></TableHead></TableRow></TableHeader>
                <TableBody>
                  {lines && lines.length > 0 ? lines.map((l) => editingLine === l.id ? (
                    <TableRow key={l.id}>
                      <TableCell><Input value={eDesc} onChange={(e) => setEDesc(e.target.value)} /></TableCell>
                      <TableCell className="w-24"><Input type="number" step="0.01" value={eQty} onChange={(e) => setEQty(e.target.value)} /></TableCell>
                      <TableCell className="w-28"><Input type="number" step="0.01" value={ePrice} onChange={(e) => setEPrice(e.target.value)} /></TableCell>
                      <TableCell className="text-right tabular-nums">{fmt(Math.round(parseFloat(eQty || "0") * Math.round(parseFloat(ePrice || "0") * 100)), inv.currency)}</TableCell>
                      <TableCell className="text-right whitespace-nowrap">
                        <Button size="icon" variant="ghost" onClick={saveLine}><Check className="h-4 w-4" /></Button>
                        <Button size="icon" variant="ghost" onClick={() => setEditingLine(null)}><X className="h-4 w-4" /></Button>
                      </TableCell>
                    </TableRow>
                  ) : (
                    <TableRow key={l.id}>
                      <TableCell>{l.description}{l.source_rule_id && <Badge variant="outline" className="ml-2 text-[10px]">auto</Badge>}</TableCell>
                      <TableCell className="text-right tabular-nums">{l.qty}</TableCell>
                      <TableCell className="text-right tabular-nums">{fmt(l.unit_price_cents, inv.currency)}</TableCell>
                      <TableCell className="text-right tabular-nums">{fmt(l.amount_cents, inv.currency)}</TableCell>
                      <TableCell className="text-right whitespace-nowrap">
                        {editable && <>
                          <Button size="icon" variant="ghost" onClick={() => startEditLine(l)}><Pencil className="h-4 w-4" /></Button>
                          <Button size="icon" variant="ghost" onClick={() => deleteLine(l.id)}><Trash2 className="h-4 w-4" /></Button>
                        </>}
                      </TableCell>
                    </TableRow>
                  )) : (<TableRow><TableCell colSpan={5} className="text-center py-6 text-muted-foreground">No lines yet.</TableCell></TableRow>)}
                </TableBody>
              </Table></div>
            </div>
          )}
        </div>
      </div>

      <AlertDialog open={!!voidId} onOpenChange={(o) => !o && setVoidId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>Void this invoice?</AlertDialogTitle><AlertDialogDescription>The invoice will be marked void and excluded from totals. This cannot be undone.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={doVoid}>Void</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <AlertDialog open={!!delId} onOpenChange={(o) => !o && setDelId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>Delete this draft invoice?</AlertDialogTitle><AlertDialogDescription>All lines on this draft will be permanently deleted.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={doDelete}>Delete</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
